import { useState } from 'react'
import { useReadNotifications, useWorkerNotifications } from '../index.js'
import type { WorkerNotification } from '../index.js'
import { WorkerMessageCard, type MessageActions } from '../components/WorkerMessageCard.js'
import { HelpIcon, LoadFailure, Skeleton } from '../../core/index.js'

/**
 * Every unexpired message, unread first, newest first within each, as
 * cards. Tapping one marks it read and shows what it was built from (spec
 * §11.5: every message can be explained) in the worker's words, never the
 * dedupe key. A kind with something to do carries its buttons on the card,
 * supplied by the shell (`renderActions`), since answering a Bridge
 * callout is the schedule module's. Expired messages are already withdrawn
 * by the API.
 */
export function WorkerMessages({ workerId, renderActions }: { workerId: string; renderActions?: MessageActions }) {
  const notificationsQuery = useWorkerNotifications(workerId, { live: true })
  const read = useReadNotifications(workerId)
  const [openId, setOpenId] = useState<string | null>(null)

  if (notificationsQuery.isPending) {
    return <Skeleton lines={5} className="p-8" />
  }
  if (notificationsQuery.isError) {
    return <LoadFailure className="p-8" message="Your messages could not be loaded." onRetry={() => void notificationsQuery.refetch()} retrying={notificationsQuery.isFetching} />
  }

  const notifications = notificationsQuery.data
  const unread = notifications.filter((notification) => !notification.read)
  const ordered = [...unread, ...notifications.filter((notification) => notification.read)]

  function open(notification: WorkerNotification): void {
    setOpenId((current) => (current === notification.id ? null : notification.id))
    if (!notification.read && !read.isPending) {
      read.mutate([notification.id])
    }
  }

  return (
    <div className="max-w-md mx-auto flex flex-col gap-3 p-4">
      <div className="flex items-baseline justify-between">
        <div className="flex items-center gap-1 text-[9px] font-bold uppercase tracking-wide text-ink-mid">{unread.length === 0 ? 'Messages' : `${unread.length} unread`}<HelpIcon topic="messages.worker-messages" /></div>
        {unread.length > 1 && (
          <button type="button" onClick={() => read.mutate(undefined)} disabled={read.isPending} className="text-[11px] font-bold text-marine hover:text-marine-dk disabled:opacity-40">
            Mark all read
          </button>
        )}
      </div>

      {read.isError && <div className="text-[11px] text-coral">That could not be saved. The message is still here.</div>}

      {ordered.length === 0 ? (
        <div className="bg-white border border-marine/15 p-4 text-[11px] text-ink-mid leading-relaxed">
          Nothing yet. A message arrives when something about your score is worth knowing: a streak, the next tier within reach, or the one thing most worth working on. At most one a day.
        </div>
      ) : (
        <ul className="flex flex-col gap-1.5">
          {ordered.map((notification) => (
            <li key={notification.id}>
              <WorkerMessageCard notification={notification} isOpen={openId === notification.id} onTap={open} actions={renderActions?.(notification)} />
            </li>
          ))}
        </ul>
      )}

      <p className="text-[10px] text-ink-mid/80 leading-relaxed">Messages come from your own shifts and nothing else. Nobody is compared with anyone.</p>
    </div>
  )
}
